import React, { useEffect, useState } from 'react';
import { FormField } from '../types/api';

interface FormProps {
  fields: FormField[];
  onSubmit: (data: Record<string, any>) => void;
  loading?: boolean;
  initialData?: Record<string, any>;
  submitText?: string;
}

const Form: React.FC<FormProps> = ({
  fields,
  onSubmit,
  loading = false,
  initialData = {},
  submitText = 'Submit'
}) => {
  const [formData, setFormData] = useState<Record<string, any>>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Keep local state in sync when initialData changes (e.g., after async load)
  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const validateField = (field: FormField, value: any): string => {
    if (field.required && (!value || value.toString().trim() === '')) {
      return `${field.label} is required`;
    }

    if (value && field.validation) {
      const { pattern, minLength, maxLength, min, max } = field.validation;

      if (pattern && !pattern.test(value.toString())) {
        return `${field.label} format is invalid`;
      }

      if (minLength && value.toString().length < minLength) {
        return `${field.label} must be at least ${minLength} characters`;
      }

      if (maxLength && value.toString().length > maxLength) {
        return `${field.label} must be at most ${maxLength} characters`;
      }

      if (min !== undefined && Number(value) < min) {
        return `${field.label} must be at least ${min}`;
      }

      if (max !== undefined && Number(value) > max) {
        return `${field.label} must be at most ${max}`;
      }
    }

    return '';
  };

  const handleInputChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    
    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors: Record<string, string> = {};
    fields.forEach(field => {
      const error = validateField(field, formData[field.name]);
      if (error) {
        newErrors[field.name] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  const renderField = (field: FormField) => {
    const value = formData[field.name] || '';
    const error = errors[field.name];

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            className={`form-textarea ${error ? 'border-red-500' : ''}`}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        );

      case 'select':
        return (
          <select
            className={`form-select ${error ? 'border-red-500' : ''}`}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
          >
            <option value="">Select {field.label.toLowerCase()}</option>
            {field.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type={field.type}
            className={`form-input ${error ? 'border-red-500' : ''}`}
            value={field.type==='date'? value? new Date(value).toISOString().split('T')[0]:'':value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {fields.map(field => (
        <div key={field.name} className="form-group">
          <label className="form-label">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {renderField(field)}
          {errors[field.name] && (
            <p className="text-red-500 text-sm mt-1">{errors[field.name]}</p>
          )}
        </div>
      ))}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Submitting...' : submitText}
        </button>
      </div>
    </form>
  );
};

export default Form; 